import { newPassword, requestPasswordResetInput, signInInput } from "@bt/domain";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2Icon, FingerprintIcon, MailCheckIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Field } from "@/components/app/field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toLanguage } from "@/i18n";
import { hasPasskeys, safeNext, useMe, welcomeUrl } from "@/lib/account";
import { errorCode, errorMessage } from "@/lib/errors";
import { navigate, PageLink, useLocation } from "@/lib/pages";
import { useTRPC } from "@/lib/trpc";
import { AuthCard } from "./account-app";
import { passkeyAutofill, passkeyCancelled, pickPasskey, takePrf } from "./passkeys";
import { SecondFactorField } from "./two-factor";

/** Field errors of a zod result, by field name. */
export function fieldErrors(issues: { path: PropertyKey[]; message: string }[]) {
  return Object.fromEntries(issues.map((i) => [String(i.path[0]), i.message]));
}

export function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p
      className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
      role="alert"
    >
      {message}
    </p>
  );
}

export function Notice({ children }: { children: string }) {
  return (
    <p
      className="mb-4 flex items-start gap-2 rounded-lg border border-ok/40 bg-ok-bg px-3 py-2 text-sm text-ok-fg"
      role="status"
    >
      <CheckCircle2Icon className="mt-0.5 size-4 shrink-0" />
      {children}
    </p>
  );
}

export function SignInPage() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const { search } = useLocation();
  const next = safeNext(search.get("next"));
  const { data: me } = useMe();
  const signIn = useMutation({ ...trpc.account.signIn.mutationOptions(), meta: { silent: true } });
  const passkeyOptions = useMutation({
    ...trpc.account.passkeySignInOptions.mutationOptions(),
    meta: { silent: true },
  });
  const passkeySignIn = useMutation({
    ...trpc.account.passkeySignIn.mutationOptions(),
    meta: { silent: true },
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Two-factor sign-in (ADR 0011): the code field shows once the server asks for it.
  const [askCode, setAskCode] = useState(false);
  const [code, setCode] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  // Already signed in: straight on.
  useEffect(() => {
    if (me?.status === "active") window.location.replace(next);
  }, [me, next]);

  /** Where a sign-in leads: the restricted state, the welcome steps, or on. */
  const enter = (result: { status: string; welcomed: boolean; passkeyOffer?: boolean }) => {
    if (result.status !== "active") window.location.assign("/account/restricted");
    else if (!result.welcomed) window.location.assign(welcomeUrl(next));
    else if (result.passkeyOffer && hasPasskeys()) {
      window.location.assign(`/account/passkey?next=${encodeURIComponent(next)}`);
    } else window.location.assign(next);
  };
  const withPasskey = async (autofill: boolean) => {
    const options = await passkeyOptions.mutateAsync();
    const response = await pickPasskey(options, autofill);
    const prf = takePrf(response);
    enter(await passkeySignIn.mutateAsync({ response: { ...response }, prf }));
  };
  const passkeyFailed = (error: unknown) => {
    if (!passkeyCancelled(error)) setServerError(errorMessage(error, t));
  };

  // The browser offers its passkeys for this site in the email field's suggestions, where it can.
  // In the background, only the server's answer is worth showing (a passkey deleted meanwhile);
  // a browser that can't do this here just leaves the suggestions out.
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, when the page opens
  useEffect(() => {
    if (!hasPasskeys()) return;
    let active = true;
    void passkeyAutofill().then((available) => {
      if (!available || !active) return;
      withPasskey(true).catch((error: unknown) => {
        if (errorCode(error)) setServerError(errorMessage(error, t));
      });
    });
    return () => {
      active = false;
    };
  }, []);

  return (
    <AuthCard title={t("account.signInTitle")} body={t("account.signInBody")}>
      {search.get("reset") ? <Notice>{t("account.resetDone")}</Notice> : null}
      {search.get("deleted") ? <Notice>{t("account.deleted")}</Notice> : null}
      <form
        className="flex flex-col gap-4"
        onSubmit={async (event) => {
          event.preventDefault();
          const parsed = signInInput.safeParse({ email, password, code: askCode ? code : null });
          if (!parsed.success) {
            setErrors(fieldErrors(parsed.error.issues));
            return;
          }
          setErrors({});
          setServerError(null);
          try {
            enter(await signIn.mutateAsync(parsed.data));
          } catch (error) {
            if (errorCode(error) === "TWO_FACTOR_REQUIRED" && !askCode) {
              setAskCode(true);
              return;
            }
            setServerError(errorMessage(error, t));
          }
        }}
      >
        <Field label={t("account.email")} htmlFor="sign-in-email" error={errors.email}>
          <Input
            id="sign-in-email"
            autoFocus
            inputMode="email"
            autoComplete={hasPasskeys() ? "username webauthn" : "username"}
            value={email}
            aria-invalid={errors.email ? true : undefined}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label={t("account.password")} htmlFor="sign-in-password" error={errors.password}>
          <Input
            id="sign-in-password"
            type="password"
            autoComplete="current-password"
            value={password}
            aria-invalid={errors.password ? true : undefined}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        {askCode ? <SecondFactorField id="sign-in-code" value={code} onChange={setCode} /> : null}
        <FormError message={serverError} />
        <Button type="submit" size="lg" disabled={signIn.isPending}>
          {t("account.signIn")}
        </Button>
        {hasPasskeys() ? (
          <Button
            type="button"
            variant="outline"
            size="lg"
            disabled={passkeySignIn.isPending}
            onClick={() => {
              setServerError(null);
              withPasskey(false).catch(passkeyFailed);
            }}
          >
            <FingerprintIcon />
            {t("account.passkeySignIn")}
          </Button>
        ) : null}
        <PageLink href="/account/forgot" className="self-center text-sm text-brand hover:underline">
          {t("account.forgot")}
        </PageLink>
      </form>
    </AuthCard>
  );
}

export function ForgotPage() {
  const { t, i18n } = useTranslation();
  const trpc = useTRPC();
  const request = useMutation({
    ...trpc.account.requestPasswordReset.mutationOptions(),
    meta: { silent: true },
  });
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [serverError, setServerError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  if (sentTo) {
    return (
      <AuthCard title={t("account.sentTitle")} body={t("account.sentBody", { email: sentTo })}>
        <div className="flex flex-col items-center gap-4">
          <MailCheckIcon className="size-8 text-brand" aria-hidden />
          <PageLink href="/account/sign-in" className="text-sm text-brand hover:underline">
            {t("account.backToSignIn")}
          </PageLink>
        </div>
      </AuthCard>
    );
  }
  return (
    <AuthCard title={t("account.forgotTitle")} body={t("account.forgotBody")}>
      <form
        className="flex flex-col gap-4"
        onSubmit={async (event) => {
          event.preventDefault();
          const parsed = requestPasswordResetInput.safeParse({
            email,
            language: toLanguage(i18n.language),
          });
          if (!parsed.success) {
            setError(fieldErrors(parsed.error.issues).email);
            return;
          }
          setError(undefined);
          try {
            await request.mutateAsync(parsed.data);
            setSentTo(parsed.data.email);
          } catch (e) {
            setServerError(errorMessage(e, t));
          }
        }}
      >
        <Field label={t("account.email")} htmlFor="forgot-email" error={error}>
          <Input
            id="forgot-email"
            autoFocus
            inputMode="email"
            autoComplete="username"
            value={email}
            aria-invalid={error ? true : undefined}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <FormError message={serverError} />
        <Button type="submit" size="lg" disabled={request.isPending}>
          {t("account.sendLink")}
        </Button>
        <PageLink
          href="/account/sign-in"
          className="self-center text-sm text-brand hover:underline"
        >
          {t("account.backToSignIn")}
        </PageLink>
      </form>
    </AuthCard>
  );
}

/** A new password typed twice, checked against the policy. */
export function NewPasswordFields({
  idPrefix,
  password,
  repeat,
  onPassword,
  onRepeat,
  errors,
  label,
}: {
  idPrefix: string;
  password: string;
  repeat: string;
  onPassword: (value: string) => void;
  onRepeat: (value: string) => void;
  errors: { password?: string; repeat?: string };
  label?: string;
}) {
  const { t } = useTranslation();
  return (
    <>
      <Field
        label={label ?? t("account.newPassword")}
        htmlFor={`${idPrefix}-password`}
        error={errors.password}
        hint={t("account.passwordHint")}
      >
        <Input
          id={`${idPrefix}-password`}
          type="password"
          autoComplete="new-password"
          value={password}
          aria-invalid={errors.password ? true : undefined}
          onChange={(e) => onPassword(e.target.value)}
        />
      </Field>
      <Field
        label={t("account.repeatPassword")}
        htmlFor={`${idPrefix}-repeat`}
        error={errors.repeat}
      >
        <Input
          id={`${idPrefix}-repeat`}
          type="password"
          autoComplete="new-password"
          value={repeat}
          aria-invalid={errors.repeat ? true : undefined}
          onChange={(e) => onRepeat(e.target.value)}
        />
      </Field>
    </>
  );
}

/** Errors of a new password and its repetition, or null when both are fine. */
export function newPasswordErrors(
  password: string,
  repeat: string,
): { password?: string; repeat?: string } | null {
  const parsed = newPassword.safeParse(password);
  if (!parsed.success) return { password: parsed.error.issues[0]?.message };
  if (password !== repeat) return { repeat: "validation.passwordsDiffer" };
  return null;
}

export function ResetPage({ token }: { token: string }) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const reset = useMutation({
    ...trpc.account.resetPassword.mutationOptions(),
    meta: { silent: true },
  });
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [errors, setErrors] = useState<{ password?: string; repeat?: string }>({});
  const [serverError, setServerError] = useState<string | null>(null);

  return (
    <AuthCard title={t("account.resetTitle")} body={t("account.resetBody")}>
      <form
        className="flex flex-col gap-4"
        onSubmit={async (event) => {
          event.preventDefault();
          const problems = newPasswordErrors(password, repeat);
          setErrors(problems ?? {});
          if (problems) return;
          try {
            await reset.mutateAsync({ token, password });
            navigate("/account/sign-in?reset=1", { replace: true });
          } catch (error) {
            setServerError(errorMessage(error, t));
          }
        }}
      >
        <NewPasswordFields
          idPrefix="reset"
          password={password}
          repeat={repeat}
          onPassword={setPassword}
          onRepeat={setRepeat}
          errors={errors}
        />
        <FormError message={serverError} />
        <Button type="submit" size="lg" disabled={reset.isPending}>
          {t("account.savePassword")}
        </Button>
        <PageLink href="/account/forgot" className="self-center text-sm text-brand hover:underline">
          {t("account.forgot")}
        </PageLink>
      </form>
    </AuthCard>
  );
}
